import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import DescriptionIcon from '@mui/icons-material/Description'
import LocationPinIcon from '@mui/icons-material/LocationPin'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import { Actions } from '@components/Actions'
import { ProjectAdres } from '@components/Adres'
import { PlanningsverschilIcon } from '@components/PlanningsverschilIcon'
import { ProjectStatus } from '@components/ProjectStatus'
import Realisatie from '@components/Realisatie'
import { formatDate } from '@util/date-util'
import { getProjectAdres } from '@util/project-util'

import { IconGridCard } from '../../../theme/components/IconGridCard'
import { Label } from '../../../theme/components/Label'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import type { Project } from '../types/Project'

export { ProjectGegevensCards }

type Props = {
  project: Project
}

const ProjectGegevensCards = ({ project }: Props) => {
  return (
    <Grid container spacing={4}>
      {/* WAT */}
      <IconGridCard icon={<DescriptionIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Project ID"
          value={project.aanvraagID}
          copy={project.aanvraagID}
          variant="h5"
        />

        <PropertyRow
          label="Processtap"
          value={<ProjectStatus status={project.statusText} />}
        />

        <PropertyRow
          label="Aanvraag compleet"
          value={project.isCompleet ? 'Ja' : 'Nee'}
        />

        <PropertyRow
          label="Acties"
          value={
            <Box sx={{ marginTop: 1 }}>
              <Actions acties={project.acties} />
            </Box>
          }
        />

        <PropertyRow
          label="Labels"
          value={project.labels?.map((label) => (
            <Label key={label} text={label} />
          ))}
        />
      </IconGridCard>

      {/* WAAR */}
      <IconGridCard icon={<LocationPinIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Adres"
          value={
            <Typography variant="h5" sx={{ display: 'inline' }}>
              <ProjectAdres {...project} />
            </Typography>
          }
          copy={getProjectAdres(project)}
        />

        <PropertyRow label="Gebied" value={project.gebiedscodeLabel} />

        <PropertyRow label="Objecten" value={project.aantalObjecten} />

        <PropertyRow label="Disciplines" value={project.uniqDisciplines} />
      </IconGridCard>

      {/* WANNEER */}
      <IconGridCard icon={<CalendarMonthIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Geplande uitvoering"
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Realisatie
                date={project.geplandeUitvoeringsdatum as string}
                type={project.geplandeUitvoeringsdatumType}
                wachtTijdDatum={project.wachtTijdDatum}
              />
              {project.planningsverschillen?.map((pv) => (
                <PlanningsverschilIcon
                  key={pv}
                  context="project"
                  planningsverschil={pv}
                />
              ))}
            </Box>
          }
        />

        <PropertyRow
          label="Aanvraagdatum"
          value={formatDate(project.aanvraagDatum)}
        />

        <PropertyRow
          label={
            project.agdRelevant ? 'Aansluitgereed klant' : 'Wensdatum klant'
          }
          value={
            project.agdRelevant
              ? formatDate(project.aansluitObjecten[0].produkten[0].agd) ||
                'Onbekend'
              : formatDate(project.wensdatum)
          }
        />
      </IconGridCard>

      {/* WIE */}
      <IconGridCard icon={<PeopleOutlineIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow label="Combi" value={project.combi} variant="h5" />

        <PropertyRow
          label="Netbeheerders"
          value={project.netbeheerders.join(', ')}
        />

        <PropertyRow
          label="Uitvoerende aannemers"
          value={project.aannemers?.join(', ')}
        />

        <PropertyRow
          label="Coördinerende aannemers"
          value={project.coordAannemer?.join(', ')}
        />
      </IconGridCard>
    </Grid>
  )
}
